import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, X, ChevronDown, ChevronLeft, ChevronRight, Search, MessageSquarePlus, Settings, ArrowUp, ImageOff, RefreshCw } from 'lucide-react';
import { useI18n } from '../i18n';
import {
  kendamaCategoryLabels,
  kendamaTypeLabels,
  KendamaModel,
  KendamaBrand,
  KendamaCategory,
  KendamaType,
} from '../kendamaCatalog';
import { User } from '../types';
import {
  fetchKendamaCatalog,
  submitKendamaSubmission,
  readFileAsDataUrl,
} from '../kendamaArchiveApi';
import { KendamaArchiveAdmin } from './KendamaArchiveAdmin';

interface KendamaArchiveProps {
  onBack: () => void;
  currentUser?: User | null;
  onRequireLogin?: (message: string) => void;
}

const categoryChipClassName: Record<KendamaCategory, string> = {
  entry: 'border-emerald-400/25 bg-emerald-500/10 text-emerald-200',
  competition: 'border-cyan-400/25 bg-cyan-500/10 text-cyan-200',
  signature: 'border-fuchsia-400/25 bg-fuchsia-500/10 text-fuchsia-200',
  limited: 'border-amber-400/25 bg-amber-500/10 text-amber-200',
  standard: 'border-sky-400/25 bg-sky-500/10 text-sky-200',
  premium: 'border-yellow-400/25 bg-yellow-500/10 text-yellow-200',
};

const uiText = {
  zh: {
    title: '万剑阁',
    subtitle: '剑玉图鉴',
    allBrands: '全部',
    brandLabel: '品牌',
    brandUnit: '个品牌',
    modelUnit: '款剑玉',
    country: '品牌属地',
    regionLabel: '属地',
    material: '材质',
    shape: '剑形',
    paint: '漆面',
    empty: '暂无收录',
    loading: '正在加载剑玉图鉴…',
    searchPlaceholder: '搜索剑玉型号',
    feedback: '反馈未收录剑玉',
    feedbackTitle: '反馈未收录剑玉',
    feedbackBrand: '剑玉品牌名',
    feedbackModel: '剑玉名称',
    feedbackImage: '上传图片（可选）',
    feedbackSubmit: '提交审核',
    feedbackCancel: '取消',
    feedbackSuccess: '反馈已提交，等待管理员审核。',
    feedbackLoginRequired: '请先登录后再提交反馈。',
    admin: '管理员',
    backToTop: '回到顶部',
    chooseImage: '选择图片',
    removeImage: '移除图片',
    noImage: '暂无图片',
    prevImage: '上一张',
    nextImage: '下一张',
  },
  en: {
    title: 'Sword Hall',
    subtitle: 'Kendama Archive',
    allBrands: 'All',
    brandLabel: 'Brand',
    brandUnit: 'brands',
    modelUnit: 'models',
    country: 'Origin',
    regionLabel: 'Origin',
    material: 'Wood',
    shape: 'Shape',
    paint: 'Paint',
    empty: 'Nothing here yet',
    loading: 'Loading archive…',
    searchPlaceholder: 'Search models',
    feedback: 'Suggest a kendama',
    feedbackTitle: 'Suggest a missing kendama',
    feedbackBrand: 'Brand name',
    feedbackModel: 'Model name',
    feedbackImage: 'Upload image (optional)',
    feedbackSubmit: 'Submit for review',
    feedbackCancel: 'Cancel',
    feedbackSuccess: 'Submitted for review.',
    feedbackLoginRequired: 'Please log in first.',
    admin: 'Admin',
    backToTop: 'Back to top',
    chooseImage: 'Choose image',
    removeImage: 'Remove image',
    noImage: 'No image',
    prevImage: 'Previous image',
    nextImage: 'Next image',
  },
  ja: {
    title: '万剣閣',
    subtitle: 'けん玉図鑑',
    allBrands: 'すべて',
    brandLabel: 'ブランド',
    brandUnit: 'ブランド',
    modelUnit: 'モデル',
    country: '産地',
    regionLabel: '産地',
    material: '材質',
    shape: 'シェイプ',
    paint: '塗装',
    empty: 'まだ収録なし',
    loading: '図鑑を読み込み中…',
    searchPlaceholder: 'モデルを検索',
    feedback: '未収録けん玉を報告',
    feedbackTitle: '未収録けん玉の報告',
    feedbackBrand: 'ブランド名',
    feedbackModel: 'けん玉名',
    feedbackImage: '画像をアップロード（任意）',
    feedbackSubmit: '審査へ送信',
    feedbackCancel: 'キャンセル',
    feedbackSuccess: '報告を送信しました。',
    feedbackLoginRequired: '先にログインしてください。',
    admin: '管理者',
    backToTop: 'トップへ戻る',
    chooseImage: '画像を選択',
    removeImage: '画像を削除',
    noImage: '画像なし',
    prevImage: '前の画像',
    nextImage: '次の画像',
  },
} as const;

type Language = 'zh' | 'en' | 'ja';

const localized = (value: { zh: string; en: string; ja: string }, language: Language) =>
  value[language] ?? value.zh;

const localizedModelName = (model: KendamaModel, language: Language) =>
  (language === 'zh' ? model.nameZh : language === 'ja' ? model.nameJa : model.name) || model.name;

const localizedBrandName = (brand: KendamaBrand, language: Language) =>
  (language === 'zh' ? brand.nameZh : language === 'ja' ? brand.nameJa : brand.name) || brand.name;

const secondaryName = (primary: string, nameZh: string, name: string, language: Language) => {
  const secondary = language === 'en' ? nameZh : name;
  return secondary && secondary !== primary ? secondary : '';
};

const archiveTypeFilterLabels: Record<KendamaType, { zh: string; en: string; ja: string }> = {
  ken: { zh: '单剑', en: 'Ken', ja: '剣' },
  tama: { zh: '单球', en: 'Tama', ja: '玉' },
  bundle: { zh: '套剑', en: 'Setup', ja: 'セット' },
  novelty: { zh: '异形剑玉', en: 'Novelty', ja: '異形けん玉' },
};

/* 品牌卡片的「转轴滚动」：以滚动容器中线为转轴，
   卡片越远离中线，绕自身水平轴滚过的角度越大，回到中线时重新摊平。
   数值刻意保持克制——卡片是又宽又扁的横条，过度的 3D 会让人读不进去。 */
const BRAND_ROLL_PERSPECTIVE_PX = 1500;
const BRAND_ROLL_MAX_TILT_DEG = 10;
const BRAND_ROLL_MAX_DEPTH_PX = 26;
const BRAND_ROLL_MAX_SHRINK = 0.03;
const BRAND_ROLL_MAX_FADE = 0.16;
/* 卡片中心离转轴多远算「滚到极限」：以容器高度的比例表示，越小越快滚满。 */
const BRAND_ROLL_AXIS_SPAN_RATIO = 0.62;
/* 完全离开视口的卡片不再参与计算，留一点余量避免边缘跳变。 */
const BRAND_ROLL_OVERSCAN_PX = 120;

// 滑动时让品牌卡片绕转轴滚动。读取全部几何信息后再统一写样式，避免逐张强制重排。
const useBrandCardRoll = (scrollRef: React.RefObject<HTMLDivElement | null>) => {
  const cardsRef = useRef(new Map<string, HTMLDivElement>());
  const refCallbacksRef = useRef(new Map<string, (node: HTMLDivElement | null) => void>());
  const frameRef = useRef<number | null>(null);
  const reducedMotionRef = useRef(false);
  /* 每张卡片的滚动强度：展开的品牌卡片会平滑摊平成 0，收起后再回到 1。 */
  const strengthRef = useRef(new WeakMap<HTMLElement, number>());

  const roll = useCallback(() => {
    frameRef.current = null;
    const container = scrollRef.current;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    if (containerRect.height <= 0) return;

    // 转轴 = 滚动容器的水平中线。
    const axisY = containerRect.top + containerRect.height / 2;
    const span = Math.max(containerRect.height * BRAND_ROLL_AXIS_SPAN_RATIO, 1);
    const reducedMotion = reducedMotionRef.current;

    let settling = false;
    const measured: Array<{ card: HTMLElement; progress: number; strength: number }> = [];
    cardsRef.current.forEach((wrapper) => {
      const card = wrapper.firstElementChild as HTMLElement | null;
      if (!card) return;

      // 展开的品牌卡片平铺显示：产品列表直接给到眼睛，不参与转轴滚动。
      const target = !reducedMotion && wrapper.dataset.rollFlat !== 'true' ? 1 : 0;
      const current = strengthRef.current.get(wrapper) ?? target;
      const strength =
        Math.abs(target - current) < 0.01 ? target : current + (target - current) * 0.16;
      strengthRef.current.set(wrapper, strength);
      if (strength !== target) settling = true;

      const rect = wrapper.getBoundingClientRect();
      const inRange =
        !reducedMotion &&
        rect.bottom >= containerRect.top - BRAND_ROLL_OVERSCAN_PX &&
        rect.top <= containerRect.bottom + BRAND_ROLL_OVERSCAN_PX;

      if (!inRange) {
        measured.push({ card, progress: Number.NaN, strength });
        return;
      }

      const raw = (rect.top + rect.height / 2 - axisY) / span;
      measured.push({ card, progress: raw < -1 ? -1 : raw > 1 ? 1 : raw, strength });
    });

    measured.forEach(({ card, progress, strength }) => {
      if (Number.isNaN(progress)) {
        card.style.transform = '';
        card.style.opacity = '';
        card.style.willChange = '';
        return;
      }

      // sin 缓动：中线附近过渡柔和，两端自然收敛到最大角度。
      const eased = Math.sin((progress * Math.PI) / 2) * strength;
      const magnitude = Math.abs(eased);
      const depth = -magnitude * BRAND_ROLL_MAX_DEPTH_PX;
      const tilt = -eased * BRAND_ROLL_MAX_TILT_DEG;
      const scale = 1 - magnitude * BRAND_ROLL_MAX_SHRINK;
      const opacity = 1 - magnitude * BRAND_ROLL_MAX_FADE;

      card.style.willChange = 'transform';
      card.style.transform = `translate3d(0, 0, ${depth.toFixed(2)}px) rotateX(${tilt.toFixed(2)}deg) scale(${scale.toFixed(4)})`;
      card.style.opacity = opacity.toFixed(3);
    });

    // 卡片展开 / 收起时再跑几帧，让摊平与恢复平滑落地，而不是硬切。
    if (settling) scheduleRoll();
  }, [scrollRef]);

  const scheduleRoll = useCallback(() => {
    if (frameRef.current !== null) return;
    frameRef.current = window.requestAnimationFrame(roll);
  }, [roll]);

  useLayoutEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const motionQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    reducedMotionRef.current = motionQuery?.matches ?? false;

    const handleMotionChange = () => {
      reducedMotionRef.current = motionQuery?.matches ?? false;
      scheduleRoll();
    };

    container.addEventListener('scroll', scheduleRoll, { passive: true });
    window.addEventListener('resize', scheduleRoll);
    motionQuery?.addEventListener?.('change', handleMotionChange);
    // 首帧同步贴一次角度，避免卡片先平铺再转起来的闪烁。
    roll();

    return () => {
      container.removeEventListener('scroll', scheduleRoll);
      window.removeEventListener('resize', scheduleRoll);
      motionQuery?.removeEventListener?.('change', handleMotionChange);
      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, [roll, scheduleRoll, scrollRef]);

  // 每张卡片一个稳定的 ref 回调，避免父组件重渲染时反复挂载 / 卸载。
  const registerCard = useCallback((id: string) => {
    let callback = refCallbacksRef.current.get(id);
    if (!callback) {
      callback = (node: HTMLDivElement | null) => {
        if (node) {
          cardsRef.current.set(id, node);
        } else {
          cardsRef.current.delete(id);
        }
      };
      refCallbacksRef.current.set(id, callback);
    }
    return callback;
  }, []);

  return { registerCard, scheduleRoll };
};

interface ModelImageGalleryProps {
  images: string[];
  alt: string;
  noImageText: string;
  prevLabel: string;
  nextLabel: string;
}

// 详情弹窗内的多图浏览：横向滑动（移动端手势 / 桌面端箭头）切换图片。
const ModelImageGallery: React.FC<ModelImageGalleryProps> = ({
  images,
  alt,
  noImageText,
  prevLabel,
  nextLabel,
}) => {
  const [index, setIndex] = useState(0);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const total = images.length;

  const goTo = (target: number) => {
    if (total === 0) return;
    const next = Math.max(0, Math.min(total - 1, target));
    const track = trackRef.current;
    if (track) {
      // 与手势滑动共用同一套滚动逻辑，保证指示器与画面同步。
      track.scrollTo({ left: next * track.clientWidth, behavior: 'smooth' });
    }
    setIndex(next);
  };

  const handleScroll = () => {
    const track = trackRef.current;
    if (!track || total === 0) return;
    const next = Math.round(track.scrollLeft / track.clientWidth);
    const clamped = Math.max(0, Math.min(total - 1, next));
    setIndex((current) => (current === clamped ? current : clamped));
  };

  return (
    <div className="relative aspect-square w-full overflow-hidden bg-white/5">
      {total > 0 ? (
        <>
          <div
            ref={trackRef}
            onScroll={handleScroll}
            className="flex h-full w-full snap-x snap-mandatory overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {images.map((src, imageIndex) => (
              <div key={`${imageIndex}-${src.slice(0, 24)}`} className="h-full w-full shrink-0 snap-center">
                <img
                  src={src}
                  alt={`${alt} ${imageIndex + 1}`}
                  draggable={false}
                  className="h-full w-full object-cover"
                />
              </div>
            ))}
          </div>

          {total > 1 && (
            <>
              <button
                type="button"
                onClick={() => goTo(index - 1)}
                aria-label={prevLabel}
                className={`absolute left-2 top-1/2 -translate-y-1/2 rounded-full border border-white/15 bg-black/50 p-1.5 text-white/80 backdrop-blur-sm transition-opacity hover:bg-black/70 hover:text-white ${
                  index === 0 ? 'pointer-events-none opacity-0' : 'opacity-100'
                }`}
              >
                <ChevronLeft size={18} />
              </button>
              <button
                type="button"
                onClick={() => goTo(index + 1)}
                aria-label={nextLabel}
                className={`absolute right-2 top-1/2 -translate-y-1/2 rounded-full border border-white/15 bg-black/50 p-1.5 text-white/80 backdrop-blur-sm transition-opacity hover:bg-black/70 hover:text-white ${
                  index === total - 1 ? 'pointer-events-none opacity-0' : 'opacity-100'
                }`}
              >
                <ChevronRight size={18} />
              </button>
              <div className="absolute bottom-2.5 left-1/2 flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-black/50 px-2.5 py-1.5 backdrop-blur-sm">
                {images.map((_, dotIndex) => (
                  <span
                    key={dotIndex}
                    className={`h-1.5 rounded-full transition-all ${
                      dotIndex === index ? 'w-4 bg-cyan-300' : 'w-1.5 bg-white/35'
                    }`}
                  />
                ))}
              </div>
              <div className="absolute right-2.5 top-2.5 rounded-full bg-black/50 px-2 py-0.5 text-[11px] text-white/80 backdrop-blur-sm">
                {index + 1} / {total}
              </div>
            </>
          )}
        </>
      ) : (
        <div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-black/40 text-zinc-600">
          <ImageOff size={28} />
          <span className="font-pingfang text-sm">{noImageText}</span>
        </div>
      )}
    </div>
  );
};

export const KendamaArchive: React.FC<KendamaArchiveProps> = ({ onBack, currentUser, onRequireLogin }) => {
  const { language } = useI18n();
  const lang = language as Language;
  const text = uiText[lang];
  const [activeBrandId, setActiveBrandId] = useState<string>('all');
  const [selectedModel, setSelectedModel] = useState<KendamaModel | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [brandOpen, setBrandOpen] = useState<boolean>(false);
  const [typeFilter, setTypeFilter] = useState<KendamaType | null>(null);
  const [materialFilter, setMaterialFilter] = useState<string | null>(null);
  const [paintFilter, setPaintFilter] = useState<string | null>(null);
  const [openType, setOpenType] = useState<KendamaType | null>(null);
  const [openAttr, setOpenAttr] = useState<'material' | 'paint' | null>(null);
  const [countryFilter, setCountryFilter] = useState<string | null>(null);
  const [countryOpen, setCountryOpen] = useState<boolean>(false);
  const [dynamicCatalog, setDynamicCatalog] = useState<KendamaBrand[]>([]);
  const [catalogLoading, setCatalogLoading] = useState<boolean>(true);
  const [expandedBrands, setExpandedBrands] = useState<Set<string>>(new Set());
  const [showFeedback, setShowFeedback] = useState<boolean>(false);
  const [showAdmin, setShowAdmin] = useState<boolean>(false);
  const [feedbackBrand, setFeedbackBrand] = useState<string>('');
  const [feedbackModel, setFeedbackModel] = useState<string>('');
  const [feedbackImage, setFeedbackImage] = useState<string>('');
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const { registerCard: registerBrandCard, scheduleRoll: scheduleBrandCardRoll } =
    useBrandCardRoll(scrollContainerRef);

  const scrollToTop = () => {
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const [feedbackSubmitting, setFeedbackSubmitting] = useState<boolean>(false);
  const [feedbackError, setFeedbackError] = useState<string>('');

  const isAdmin = !!currentUser && !!(currentUser.isAdmin || currentUser.roles?.includes('administrator'));

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    setCatalogLoading(true);
    fetchKendamaCatalog(controller.signal)
      .then((catalog) => {
        if (!cancelled) setDynamicCatalog(catalog);
      })
      .catch(() => {
        if (!cancelled) setDynamicCatalog([]);
      })
      .finally(() => {
        if (!cancelled) setCatalogLoading(false);
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  const mergedBrands = dynamicCatalog;

  const toggleBrand = (brandId: string) => {
    setExpandedBrands((previous) => {
      if (previous.has(brandId)) {
        const next = new Set(previous);
        next.delete(brandId);
        return next;
      }
      return new Set([brandId]);
    });
  };

  const typeOrder: KendamaType[] = ['ken', 'tama', 'bundle'];
  const typeAttrs: Partial<Record<KendamaType, ('material' | 'paint')[]>> = {
    ken: ['material'],
    tama: ['material', 'paint'],
  };
  const typeAttrLabels: Record<'material' | 'paint', string> = {
    material: text.material,
    paint: text.paint,
  };

  const totalModels = useMemo(
    () => mergedBrands.reduce((sum, brand) => sum + brand.models.length, 0),
    [mergedBrands]
  );

  const allModels = useMemo(() => mergedBrands.flatMap((brand) => brand.models), [mergedBrands]);

  const attrOptions = useMemo(() => {
    const pool = typeFilter ? allModels.filter((model) => model.type === typeFilter) : allModels;
    const uniq = (values: string[]) => Array.from(new Set(values));
    return {
      material: uniq(
        pool.filter((model) => model.type === 'ken' || model.type === 'tama').map((model) => model.material)
      ),
      paint: uniq(
        pool.filter((model) => model.type === 'tama').map((model) => model.paint ?? '').filter(Boolean)
      ),
    };
  }, [allModels, typeFilter]);

  const countryOptions = useMemo(() => {
    const map = new Map<string, { zh: string; en: string; ja: string }>();
    mergedBrands.forEach((brand) => {
      if (!map.has(brand.country)) {
        map.set(brand.country, { zh: brand.countryZh, en: brand.country, ja: brand.countryJa });
      }
    });
    return Array.from(map.entries()).map(([value, label]) => ({ value, label }));
  }, [mergedBrands]);

  const handleTypeClick = (type: KendamaType) => {
    if (typeFilter === type) {
      setTypeFilter(null);
      setOpenType(null);
    } else {
      setTypeFilter(type);
      setOpenType(type);
    }
    setOpenAttr(null);
    setMaterialFilter(null);
    setPaintFilter(null);
  };

  const currentAttrValue = (attr: 'material' | 'paint') => {
    if (attr === 'material') return materialFilter;
    return paintFilter;
  };

  const selectAttr = (attr: 'material' | 'paint', value: string | null) => {
    if (attr === 'material') setMaterialFilter(value);
    else setPaintFilter(value);
    setOpenAttr(null);
  };

  const visibleBrands = useMemo(() => {
    const brands =
      (activeBrandId === 'all'
        ? mergedBrands
        : mergedBrands.filter((brand) => brand.id === activeBrandId))
        .filter((brand) => !countryFilter || brand.country === countryFilter);
    const query = searchQuery.trim().toLowerCase();

    const matchesQuery = (model: KendamaModel) => {
      if (typeFilter && model.type !== typeFilter) return false;
      if (materialFilter && model.material !== materialFilter) return false;
      if (paintFilter && model.paint !== paintFilter) return false;
      if (!query) return true;
      const haystack = [model.name, model.nameZh, model.nameJa, model.shape, model.material, ...model.tags];
      if (model.paint) haystack.push(model.paint);
      return haystack.join(' ').toLowerCase().includes(query);
    };

    return brands
      .map((brand) => ({ ...brand, models: brand.models.filter(matchesQuery) }))
      .filter((brand) => brand.models.length > 0);
  }, [activeBrandId, typeFilter, materialFilter, paintFilter, countryFilter, searchQuery, mergedBrands]);

  const activeBrand =
    activeBrandId === 'all' ? null : mergedBrands.find((brand) => brand.id === activeBrandId) ?? null;

  const activeBrandName = activeBrand ? localizedBrandName(activeBrand, lang) : text.brandLabel;

  const currentCountryName = countryFilter
    ? localized(
        countryOptions.find((option) => option.value === countryFilter)?.label ?? {
          zh: countryFilter,
          en: countryFilter,
          ja: countryFilter,
        },
        lang
      )
    : text.regionLabel;

  const selectedBrand = selectedModel
    ? mergedBrands.find((brand) => brand.models.some((model) => model.id === selectedModel.id)) ?? null
    : null;

  const selectedModelAttrs = selectedModel && selectedBrand
    ? (() => {
        const attrs: { label: string; value: string }[] = [];
        attrs.push({ label: text.material, value: selectedModel.material });
        if (selectedModel.type === 'tama' && selectedModel.paint) {
          attrs.push({ label: text.paint, value: selectedModel.paint });
        }
        attrs.push({
          label: text.country,
          value: localized({ zh: selectedBrand.countryZh, en: selectedBrand.country, ja: selectedBrand.countryJa }, lang),
        });
        return attrs;
      })()
    : [];

  const openFeedback = () => {
    setFeedbackBrand('');
    setFeedbackModel('');
    setFeedbackImage('');
    setFeedbackError('');
    setShowFeedback(true);
  };

  const handleFeedbackImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setFeedbackError('请选择图片文件。');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setFeedbackError('图片请控制在 5MB 以内。');
      return;
    }
    try {
      const dataUrl = await readFileAsDataUrl(file);
      setFeedbackImage(dataUrl);
      setFeedbackError('');
    } catch (err) {
      setFeedbackError(err instanceof Error ? err.message : '图片读取失败，请重试。');
    }
  };

  const handleSubmitFeedback = async () => {
    if (!currentUser) {
      setShowFeedback(false);
      onRequireLogin?.(text.feedbackLoginRequired);
      return;
    }
    const brandName = feedbackBrand.trim();
    const modelName = feedbackModel.trim();
    if (!brandName || !modelName) {
      setFeedbackError('请填写剑玉品牌名和剑玉名称。');
      return;
    }
    setFeedbackSubmitting(true);
    setFeedbackError('');
    try {
      await submitKendamaSubmission({
        brandName,
        modelName,
        image: feedbackImage || undefined,
      });
      setShowFeedback(false);
    } catch (err) {
      setFeedbackError(err instanceof Error ? err.message : '提交反馈失败。');
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  // 品牌卡片增删、展开收起、筛选与搜索结果变化后，重新贴合转轴轨迹。
  useLayoutEffect(() => {
    scheduleBrandCardRoll();
  }, [scheduleBrandCardRoll, visibleBrands, expandedBrands]);

  return (
    <div className="relative isolate flex min-h-0 w-full flex-1 flex-col overflow-hidden text-white">
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 -z-10 bg-[radial-gradient(circle_at_top,_rgba(34,211,238,0.12),_transparent_28%),linear-gradient(180deg,_rgba(12,14,20,0.98),_rgba(5,6,10,1))]"
      />

      {/* 顶部标题与筛选区固定，只有下面的品牌卡片列表滚动 */}
      <div className="mx-auto flex min-h-0 w-full max-w-7xl flex-1 flex-col gap-3 px-3 py-3 md:px-6 md:py-5">
        {/* Header */}
        <div className="relative flex min-h-[44px] shrink-0 items-center justify-center px-12 pt-1 text-center">
          <button onClick={onBack} className="absolute left-0 p-2 text-white/50 transition-colors hover:text-white" aria-label="Back">
            <ArrowLeft size={24} />
          </button>
          {isAdmin && (
            <button
              onClick={() => setShowAdmin(true)}
              className="absolute right-0 p-2 text-white/50 transition-colors hover:text-white"
              aria-label={text.admin}
              title={text.admin}
            >
              <Settings size={20} />
            </button>
          )}
          <div className="flex flex-col items-center">
            <div className="flex items-center gap-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.6}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-5 w-5 text-cyan-300"
                aria-hidden="true"
              >
                <circle cx="12" cy="6.6" r="4.2" />
                <path d="M9.3 4.6c.6-1 1.7-1.7 2.9-1.8" />
                <path d="M7.2 8.7H5.5c-.8 0-1.5.7-1.5 1.5v4.2c0 .8.7 1.5 1.5 1.5h1.3" />
                <path d="M16.8 8.7h1.7c.8 0 1.5.7 1.5 1.5v4.2c0 .8-.7 1.5-1.5 1.5h-1.3" />
                <path d="M7.2 8.7v7" />
                <path d="M16.8 8.7v7" />
                <path d="M7.2 15.1c1.5-1.1 3-1.7 4.8-1.7s3.3.6 4.8 1.7" />
                <path d="M10.8 13.8 10 18.4" />
                <path d="M13.2 13.8 14 18.4" />
                <path d="M9.7 18.4h4.6" />
                <path d="M9.4 18.4c-.5.5-.8 1.2-.8 2v1.2c0 .6.5 1.1 1.1 1.1h4.6c.6 0 1.1-.5 1.1-1.1v-1.2c0-.8-.3-1.5-.8-2" />
                <path d="M8.5 19.8h7" />
                <path d="M8.2 22.7h7.6" />
                <path d="M12.3 10.6 11.2 11.9" />
              </svg>
              <h1 className="font-pingfang text-xl font-bold tracking-[0.14em] text-cyan-300 drop-shadow-[0_0_14px_rgba(34,211,238,0.38)] md:text-3xl">
                {text.title}
              </h1>
            </div>
            <div className="mt-1 font-orbitron text-[10px] uppercase tracking-[0.28em] text-cyan-100/50 md:text-xs">
              {text.subtitle} · {mergedBrands.length} {text.brandUnit} · {totalModels} {text.modelUnit}
            </div>
          </div>
        </div>

        {/* Search & Brand filter */}
        <div className="flex shrink-0 flex-col gap-2 sm:flex-row sm:items-center">
          <div className="relative min-w-0 flex-1 sm:max-w-xs">
            <Search
              size={16}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-white/40"
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder={text.searchPlaceholder}
              className="w-full rounded-full border border-white/10 bg-white/5 py-1.5 pl-9 pr-3 text-xs text-white outline-none transition-colors placeholder:text-zinc-500 focus:border-cyan-300/50 focus:bg-white/[0.07] md:text-sm"
            />
          </div>

          <div className="relative shrink-0">
            <button
              type="button"
              onClick={() => setBrandOpen((open) => !open)}
              className="flex w-full items-center justify-between gap-2 rounded-full border border-cyan-300/60 bg-cyan-500/20 px-4 py-1.5 text-xs font-medium text-cyan-100 shadow-[0_0_14px_rgba(34,211,238,0.24)] transition-colors hover:bg-cyan-500/25 sm:w-auto"
            >
              <span className="truncate">{activeBrandName}</span>
              <ChevronDown
                size={14}
                className={`shrink-0 transition-transform ${brandOpen ? 'rotate-180' : ''}`}
              />
            </button>

            {brandOpen && (
              <>
                <div className="fixed inset-0 z-20" onClick={() => setBrandOpen(false)} />
                <div className="absolute left-0 right-0 top-full z-30 mt-2 max-h-64 overflow-y-auto rounded-2xl border border-white/10 bg-[#0c0e14] p-1.5 shadow-2xl custom-scrollbar sm:right-auto sm:w-64">
                  <button
                    type="button"
                    onClick={() => {
                      setActiveBrandId('all');
                      setBrandOpen(false);
                    }}
                    className={`flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-xs transition-colors ${
                      activeBrandId === 'all'
                        ? 'bg-cyan-500/15 text-cyan-100'
                        : 'text-zinc-300 hover:bg-white/5'
                    }`}
                  >
                    <span className="truncate">{text.allBrands}</span>
                  </button>
                  {mergedBrands.map((brand) => (
                    <button
                      key={brand.id}
                      type="button"
                      onClick={() => {
                        setActiveBrandId(brand.id);
                        setBrandOpen(false);
                      }}
                      className={`flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-xs transition-colors ${
                        activeBrandId === brand.id
                          ? 'bg-cyan-500/15 text-cyan-100'
                          : 'text-zinc-300 hover:bg-white/5'
                      }`}
                    >
                      <span
                        className="h-2 w-2 shrink-0 rounded-full"
                        style={{ backgroundColor: brand.accent }}
                      />
                      <span className="truncate">{localizedBrandName(brand, lang)}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Type filter */}
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {typeOrder.map((type) => {
            const isActive = typeFilter === type;
            const isOpen = openType === type;
            const attrs = typeAttrs[type];
            return (
              <div key={type} className="relative">
                <button
                  type="button"
                  onClick={() => handleTypeClick(type)}
                  className={`flex items-center gap-1.5 rounded-full border px-4 py-1.5 text-xs font-medium transition-colors ${
                    isActive
                      ? 'border-cyan-300/60 bg-cyan-500/20 text-cyan-100 shadow-[0_0_14px_rgba(34,211,238,0.24)]'
                      : 'border-white/10 bg-white/5 text-zinc-300 hover:border-white/20 hover:bg-white/10'
                  }`}
                >
                  <span>{localized(archiveTypeFilterLabels[type], lang)}</span>
                  {attrs && (
                    <ChevronDown
                      size={14}
                      className={`shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                    />
                  )}
                </button>

                {isOpen && attrs && (
                  <>
                    <div className="fixed inset-0 z-20" onClick={() => setOpenType(null)} />
                    <div className="absolute left-0 top-full z-30 mt-2 w-60 rounded-2xl border border-white/10 bg-[#0c0e14] p-2 shadow-2xl">
                      {attrs.map((attr) => {
                        const attrOpen = openAttr === attr;
                        const attrValue = currentAttrValue(attr);
                        return (
                          <div key={attr} className="relative">
                            <button
                              type="button"
                              onClick={() => setOpenAttr((cur) => (cur === attr ? null : attr))}
                              className="flex w-full items-center justify-between gap-2 rounded-xl px-3 py-2 text-xs text-zinc-300 transition-colors hover:bg-white/5"
                            >
                              <span>{typeAttrLabels[attr]}</span>
                              <span className="flex min-w-0 items-center gap-1 text-zinc-500">
                                <span className="truncate">{attrValue ?? text.allBrands}</span>
                                <ChevronDown
                                  size={12}
                                  className={`shrink-0 transition-transform ${attrOpen ? 'rotate-180' : ''}`}
                                />
                              </span>
                            </button>

                            {attrOpen && (
                              <div className="mb-1 mt-1 space-y-0.5 rounded-xl bg-black/30 p-1">
                                <button
                                  type="button"
                                  onClick={() => selectAttr(attr, null)}
                                  className={`flex w-full items-center rounded-lg px-3 py-1.5 text-left text-xs transition-colors ${
                                    attrValue === null
                                      ? 'bg-cyan-500/15 text-cyan-100'
                                      : 'text-zinc-400 hover:bg-white/5 hover:text-white'
                                  }`}
                                >
                                  {text.allBrands}
                                </button>
                                {attrOptions[attr].map((option) => (
                                  <button
                                    key={option}
                                    type="button"
                                    onClick={() => selectAttr(attr, option)}
                                    className={`flex w-full items-center rounded-lg px-3 py-1.5 text-left text-xs transition-colors ${
                                      attrValue === option
                                        ? 'bg-cyan-500/15 text-cyan-100'
                                        : 'text-zinc-300 hover:bg-white/5'
                                    }`}
                                  >
                                    {option}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            );
          })}

          <div className="relative">
            <button
              type="button"
              onClick={() => setCountryOpen((open) => !open)}
              className={`flex items-center gap-1.5 rounded-full border px-4 py-1.5 text-xs font-medium transition-colors ${
                countryFilter
                  ? 'border-cyan-300/60 bg-cyan-500/20 text-cyan-100 shadow-[0_0_14px_rgba(34,211,238,0.24)]'
                  : 'border-white/10 bg-white/5 text-zinc-300 hover:border-white/20 hover:bg-white/10'
              }`}
            >
              <span>{currentCountryName}</span>
              <ChevronDown
                size={14}
                className={`shrink-0 transition-transform ${countryOpen ? 'rotate-180' : ''}`}
              />
            </button>

            {countryOpen && (
              <>
                <div className="fixed inset-0 z-20" onClick={() => setCountryOpen(false)} />
                <div className="absolute left-0 top-full z-30 mt-2 w-48 rounded-2xl border border-white/10 bg-[#0c0e14] p-1.5 shadow-2xl">
                  <button
                    type="button"
                    onClick={() => {
                      setCountryFilter(null);
                      setCountryOpen(false);
                    }}
                    className={`flex w-full items-center rounded-xl px-3 py-2 text-left text-xs transition-colors ${
                      countryFilter === null
                        ? 'bg-cyan-500/15 text-cyan-100'
                        : 'text-zinc-300 hover:bg-white/5'
                    }`}
                  >
                    <span className="truncate">{text.allBrands}</span>
                  </button>
                  {countryOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => {
                        setCountryFilter(option.value);
                        setCountryOpen(false);
                      }}
                      className={`flex w-full items-center rounded-xl px-3 py-2 text-left text-xs transition-colors ${
                        countryFilter === option.value
                          ? 'bg-cyan-500/15 text-cyan-100'
                          : 'text-zinc-300 hover:bg-white/5'
                      }`}
                    >
                      <span className="truncate">{localized(option.label, lang)}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="relative">
            <button
              type="button"
              onClick={() => handleTypeClick('novelty')}
              className={`flex items-center gap-1.5 rounded-full border px-4 py-1.5 text-xs font-medium transition-colors ${
                typeFilter === 'novelty'
                  ? 'border-cyan-300/60 bg-cyan-500/20 text-cyan-100 shadow-[0_0_14px_rgba(34,211,238,0.24)]'
                  : 'border-white/10 bg-white/5 text-zinc-300 hover:border-white/20 hover:bg-white/10'
              }`}
            >
              <span>{localized(kendamaTypeLabels['novelty'], lang)}</span>
            </button>
          </div>
        </div>

        {/* 品牌卡片列表：滚动与转轴效果都只发生在这里 */}
        <div
          ref={scrollContainerRef}
          className="flex min-h-0 w-full flex-1 flex-col gap-3 overflow-y-auto overflow-x-hidden pb-[calc(6.5rem_+_var(--safe-bottom))] md:pb-24 custom-scrollbar"
        >
          {/* Brand sections */}
          {catalogLoading ? (
            <div className="flex min-h-[40vh] flex-col items-center justify-center gap-3 text-sm text-zinc-400">
              <RefreshCw size={22} className="animate-spin text-cyan-300" />
              <span>{text.loading}</span>
            </div>
          ) : visibleBrands.length > 0 ? (
            <div className="flex flex-col gap-5">
              {visibleBrands.map((brand) => (
                <div
                  key={brand.id}
                  ref={registerBrandCard(brand.id)}
                  data-roll-flat={expandedBrands.has(brand.id) ? 'true' : 'false'}
                  className="w-full"
                  style={{ perspective: `${BRAND_ROLL_PERSPECTIVE_PX}px` }}
                >
                  <section className="rounded-3xl border border-white/10 bg-black/40 p-3 shadow-[0_0_35px_rgba(34,211,238,0.06)] md:p-5">
                    <button
                      type="button"
                      onClick={() => toggleBrand(brand.id)}
                      className="flex w-full items-center gap-3 text-left transition-transform duration-150 ease-out active:scale-[0.99]"
                    >
                      <span
                        className="h-9 w-1.5 shrink-0 rounded-full transition-colors"
                        style={
                          expandedBrands.has(brand.id)
                            ? { backgroundColor: brand.accent, boxShadow: `0 0 14px ${brand.accent}66` }
                            : { backgroundColor: '#9ca3af', boxShadow: '0 0 14px rgba(156,163,175,0.35)' }
                        }
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                          <h2 className="font-orbitron text-base font-bold uppercase tracking-[0.1em] text-white md:text-lg">
                            {localizedBrandName(brand, lang)}
                          </h2>
                          {secondaryName(localizedBrandName(brand, lang), brand.nameZh, brand.name, lang) && (
                            <span className="font-pingfang text-sm font-medium text-zinc-300">
                              {secondaryName(localizedBrandName(brand, lang), brand.nameZh, brand.name, lang)}
                            </span>
                          )}
                        </div>
                        <div className="mt-0.5 flex items-center gap-2 text-[11px] text-zinc-400">
                          <span>{localized({ zh: brand.countryZh, en: brand.country, ja: brand.countryJa }, lang)}</span>
                          <span className="text-white/20">·</span>
                          <span>{brand.models.length} {text.modelUnit}</span>
                        </div>
                      </div>
                      <ChevronDown
                        size={18}
                        className={`shrink-0 text-zinc-500 transition-transform ${expandedBrands.has(brand.id) ? 'rotate-180' : ''}`}
                      />
                    </button>
    
                    {expandedBrands.has(brand.id) && (
                      <>
                        <p className="mt-2 text-xs leading-relaxed text-zinc-400 md:text-sm">
                          {localized(brand.description, lang)}
                        </p>
    
                        <div className="mt-3 grid grid-cols-2 gap-2 sm:gap-3">
                          {brand.models.map((model) => (
                            <button
                              key={model.id}
                              type="button"
                              onClick={() => setSelectedModel(model)}
                              className="group flex flex-col overflow-hidden rounded-xl border border-white/10 bg-white/[0.03] text-left transition-[background-color,border-color,transform] duration-200 ease-out hover:border-white/20 hover:bg-white/[0.06] active:scale-[0.98]"
                            >
                              <div className="aspect-square w-full overflow-hidden bg-white/5">
                                {model.image ? (
                                  <img
                                    src={model.image}
                                    alt={model.name}
                                    loading="lazy"
                                    onLoad={scheduleBrandCardRoll}
                                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                                  />
                                ) : (
                                  <div className="flex h-full w-full flex-col items-center justify-center gap-1.5 bg-black/40 text-zinc-600">
                                    <ImageOff size={22} />
                                    <span className="font-pingfang text-[11px]">{text.noImage}</span>
                                  </div>
                                )}
                              </div>
                              <div className="min-w-0 p-2">
                                <div className="truncate font-orbitron text-xs font-bold tracking-[0.03em] text-white">
                                  {localizedModelName(model, lang)}
                                </div>
                                {secondaryName(localizedModelName(model, lang), model.nameZh, model.name, lang) && (
                                  <div className="mt-0.5 truncate font-pingfang text-[11px] text-zinc-400">
                                    {secondaryName(localizedModelName(model, lang), model.nameZh, model.name, lang)}
                                  </div>
                                )}
                              </div>
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </section>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex min-h-[40vh] items-center justify-center text-sm text-zinc-500">{text.empty}</div>
          )}
        </div>
      </div>

      {/* Model detail modal */}
      {selectedModel && selectedBrand && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" onClick={() => setSelectedModel(null)}>
          <div
            className="max-h-[85vh] w-full max-w-lg overflow-x-hidden overflow-y-auto rounded-3xl border border-white/10 bg-[#0c0e14] shadow-2xl custom-scrollbar"
            onClick={(event) => event.stopPropagation()}
          >
            <ModelImageGallery
              key={selectedModel.id}
              images={selectedModel.images?.length ? selectedModel.images : selectedModel.image ? [selectedModel.image] : []}
              alt={selectedModel.name}
              noImageText={text.noImage}
              prevLabel={text.prevImage}
              nextLabel={text.nextImage}
            />

            <div className="p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[11px] font-orbitron uppercase tracking-[0.2em] text-cyan-300/70">
                    {localizedBrandName(selectedBrand, lang)}
                  </div>
                  <h3 className="mt-1 font-orbitron text-xl font-bold text-white">{localizedModelName(selectedModel, lang)}</h3>
                  {secondaryName(localizedModelName(selectedModel, lang), selectedModel.nameZh, selectedModel.name, lang) && (
                    <div className="mt-0.5 font-pingfang text-sm text-zinc-300">
                      {secondaryName(localizedModelName(selectedModel, lang), selectedModel.nameZh, selectedModel.name, lang)}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedModel(null)}
                  className="shrink-0 rounded-full border border-white/10 p-1.5 text-zinc-400 transition-colors hover:border-white/20 hover:text-white"
                  aria-label="Close"
                >
                  <X size={16} />
                </button>
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                <span className={`rounded-full border px-2.5 py-1 text-[11px] font-medium ${categoryChipClassName[selectedModel.category]}`}>
                  {localized(kendamaCategoryLabels[selectedModel.category], lang)}
                </span>
                {selectedModel.tags.map((tag) => (
                  <span key={tag} className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] text-zinc-300">
                    {tag}
                  </span>
                ))}
              </div>

              <div className={`mt-4 grid gap-2 ${selectedModelAttrs.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
                {selectedModelAttrs.map((attr) => (
                  <div key={attr.label} className="rounded-xl border border-white/10 bg-white/[0.03] p-3 text-center">
                    <div className="text-[10px] uppercase tracking-[0.12em] text-zinc-500">{attr.label}</div>
                    <div className="mt-1 font-pingfang text-sm text-white">{attr.value}</div>
                  </div>
                ))}
              </div>

              <p className="mt-4 text-sm leading-relaxed text-zinc-300">
                {localized(selectedModel.description, lang)}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Floating action buttons */}
      <div className="fixed bottom-[calc(1rem_+_var(--safe-bottom))] right-4 z-40 flex flex-col items-end gap-3 md:bottom-6 md:right-6">
        <button
          type="button"
          onClick={scrollToTop}
          aria-label={text.backToTop}
          title={text.backToTop}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/10 text-zinc-200 shadow-[0_0_18px_rgba(255,255,255,0.08)] backdrop-blur-sm transition-colors hover:bg-white/20 hover:text-white"
        >
          <ArrowUp size={18} />
        </button>

        <button
          type="button"
          onClick={openFeedback}
          className="flex items-center gap-2 rounded-full border border-cyan-300/60 bg-cyan-500/25 px-4 py-2.5 text-xs font-medium text-cyan-100 shadow-[0_0_18px_rgba(34,211,238,0.35)] backdrop-blur-sm transition-colors hover:bg-cyan-500/35"
        >
          <MessageSquarePlus size={16} />
          <span>{text.feedback}</span>
        </button>
      </div>

      {/* Feedback modal */}
      {showFeedback && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
          onClick={() => setShowFeedback(false)}
        >
          <div
            className="w-full max-w-md rounded-3xl border border-white/10 bg-[#0c0e14] p-5 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3">
              <h3 className="font-pingfang text-lg font-bold text-white">{text.feedbackTitle}</h3>
              <button
                type="button"
                onClick={() => setShowFeedback(false)}
                className="shrink-0 rounded-full border border-white/10 p-1.5 text-zinc-400 transition-colors hover:border-white/20 hover:text-white"
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>

            <div className="mt-4 flex flex-col gap-3">
              <div>
                <label className="mb-1 block text-xs text-zinc-400">{text.feedbackBrand}</label>
                <input
                  type="text"
                  value={feedbackBrand}
                  onChange={(event) => setFeedbackBrand(event.target.value)}
                  className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-white outline-none transition-colors placeholder:text-zinc-500 focus:border-cyan-300/50 focus:bg-white/[0.07]"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-zinc-400">{text.feedbackModel}</label>
                <input
                  type="text"
                  value={feedbackModel}
                  onChange={(event) => setFeedbackModel(event.target.value)}
                  className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-white outline-none transition-colors placeholder:text-zinc-500 focus:border-cyan-300/50 focus:bg-white/[0.07]"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-zinc-400">{text.feedbackImage}</label>
                <input
                  id="kendama-feedback-image"
                  type="file"
                  accept="image/*"
                  onChange={handleFeedbackImageChange}
                  className="hidden"
                />
                {feedbackImage ? (
                  <div className="flex items-center gap-3">
                    <img
                      src={feedbackImage}
                      alt="预览"
                      className="h-16 w-16 rounded-xl border border-white/10 bg-white/5 object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => setFeedbackImage('')}
                      className="text-xs text-zinc-400 underline-offset-2 transition-colors hover:text-white hover:underline"
                    >
                      {text.removeImage}
                    </button>
                  </div>
                ) : (
                  <label
                    htmlFor="kendama-feedback-image"
                    className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-zinc-300 transition-colors hover:bg-white/10"
                  >
                    {text.chooseImage}
                  </label>
                )}
              </div>

              {feedbackError && (
                <div className="rounded-xl border border-rose-400/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">
                  {feedbackError}
                </div>
              )}

              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowFeedback(false)}
                  className="rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-xs text-zinc-300 transition-colors hover:bg-white/10"
                >
                  {text.feedbackCancel}
                </button>
                <button
                  type="button"
                  onClick={handleSubmitFeedback}
                  disabled={feedbackSubmitting}
                  className="rounded-full border border-cyan-300/60 bg-cyan-500/20 px-4 py-1.5 text-xs font-medium text-cyan-100 transition-colors hover:bg-cyan-500/25 disabled:opacity-50"
                >
                  {feedbackSubmitting ? '…' : text.feedbackSubmit}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Admin panel */}
      {showAdmin && (
        <div className="fixed inset-0 z-50 overflow-hidden pt-[var(--safe-top)]">
          <KendamaArchiveAdmin
            onBack={() => setShowAdmin(false)}
            onCatalogChanged={(catalog) => setDynamicCatalog(catalog)}
          />
        </div>
      )}
    </div>
  );
};
